import {
  AudioQuality,
  IOSOutputFormat,
  getRecordingPermissionsAsync,
  requestRecordingPermissionsAsync,
  setAudioModeAsync,
  type RecordingOptions,
} from 'expo-audio';

export type MicrophonePermissionStatus =
  | 'granted'
  | 'denied'
  | 'undetermined';

type VoiceSessionPhase = 'idle' | 'recording' | 'playback';

function mapStatus(response: {
  granted: boolean;
  canAskAgain?: boolean;
  status: string;
}): MicrophonePermissionStatus {
  if (response.granted) return 'granted';
  if (response.canAskAgain === false || response.status === 'denied') {
    return 'denied';
  }
  return 'undetermined';
}

export async function getMicrophonePermissionStatus(): Promise<MicrophonePermissionStatus> {
  return mapStatus(await getRecordingPermissionsAsync());
}

export async function requestMicrophonePermission(): Promise<MicrophonePermissionStatus> {
  return mapStatus(await requestRecordingPermissionsAsync());
}

const RECORDING_MODE = {
  playsInSilentMode: true,
  allowsRecording: true,
  interruptionMode: 'duckOthers',
  shouldPlayInBackground: false,
  // Also keeps iOS playback on the speaker while recording is allowed
  // (expo-audio adds defaultToSpeaker).
  shouldRouteThroughEarpiece: false,
} as const;

const PLAYBACK_MODE = { ...RECORDING_MODE, allowsRecording: false } as const;

const IDLE_MODE = {
  playsInSilentMode: false,
  allowsRecording: false,
  interruptionMode: 'mixWithOthers',
  shouldPlayInBackground: false,
  shouldRouteThroughEarpiece: false,
} as const;

/** The mode last applied, set only once the native call has resolved. */
let currentPhase: VoiceSessionPhase = 'idle';
/** Live mode conversations holding the session in recording mode. */
let conversationHolds = 0;
/**
 * Every mode change runs here, in call order, and decides when it runs, not
 * when it was asked for: a slow "back to normal" from a finished reply can't
 * land after the recording that followed it. A failed call doesn't block the
 * next one.
 */
let modeQueue: Promise<void> = Promise.resolve();

function enqueue(op: () => Promise<void>): Promise<void> {
  const run = modeQueue.then(op);
  modeQueue = run.catch(() => undefined);
  return run;
}

// Activates the audio session for recording with OS-audio ducking. Idempotent.
export function activateVoiceSession(): Promise<void> {
  return enqueue(async () => {
    if (currentPhase === 'recording') return;
    await setAudioModeAsync(RECORDING_MODE);
    currentPhase = 'recording';
  });
}

// Activates the audio session for TTS playback only: keeps background music ducked
// so the agent's voice plays over Spotify/YouTube without stopping them. During a
// held conversation the recording mode stays (no route change between turns).
export function activateVoicePlaybackSession(): Promise<void> {
  return enqueue(async () => {
    if (conversationHolds > 0 || currentPhase === 'playback') return;
    await setAudioModeAsync(PLAYBACK_MODE);
    currentPhase = 'playback';
  });
}

// Restores normal audio routing after voice mode exits. Idempotent; waits for
// the last held conversation to end.
export function deactivateVoiceSession(): Promise<void> {
  return enqueue(async () => {
    if (conversationHolds > 0 || currentPhase === 'idle') return;
    await setAudioModeAsync(IDLE_MODE);
    currentPhase = 'idle';
  });
}

/**
 * Applies the recording mode again even if it is already current: the
 * recorder's retry after a contended prepare, which used to cycle through
 * the idle mode (impossible while a conversation holds the session).
 */
export function reassertVoiceSession(): Promise<void> {
  return enqueue(async () => {
    await setAudioModeAsync(RECORDING_MODE);
    currentPhase = 'recording';
  });
}

/**
 * Live mode keeps one recording session for the whole conversation instead
 * of switching the iOS category on every turn (record, idle, playback, idle),
 * which also bounces Bluetooth headsets between profiles. While held, the
 * playback and idle switches above do nothing; the last release restores
 * normal routing. Returns that release, safe to call more than once.
 */
export function holdConversationAudio(): () => Promise<void> {
  conversationHolds += 1;
  void activateVoiceSession().catch(() => undefined);
  let released = false;
  return () => {
    if (released) return Promise.resolve();
    released = true;
    conversationHolds -= 1;
    return deactivateVoiceSession();
  };
}

export function getVoiceSessionPhase(): VoiceSessionPhase {
  return currentPhase;
}

// 16 kHz mono AAC: plenty for Gemini 3.5 Transcribe, small to upload. Voice-recognition
// audio source on Android applies echo cancellation + AGC.
export const VOICE_RECORDING_OPTIONS: RecordingOptions = {
  isMeteringEnabled: true,
  extension: '.m4a',
  sampleRate: 16000,
  numberOfChannels: 1,
  // AAC at 16 kHz mono tops out below 64 kbps: iOS refuses to initialise the
  // encoder at 64000 ('brat' AudioCodecInitialize failed), so recording never
  // starts. 32 kbps is well inside the range and plenty for speech.
  bitRate: 32000,
  android: {
    extension: '.m4a',
    outputFormat: 'mpeg4',
    audioEncoder: 'aac',
    audioSource: 'voice_recognition',
  },
  ios: {
    extension: '.m4a',
    outputFormat: IOSOutputFormat.MPEG4AAC,
    audioQuality: AudioQuality.MEDIUM,
    linearPCMBitDepth: 16,
    linearPCMIsBigEndian: false,
    linearPCMIsFloat: false,
  },
  web: {
    mimeType: 'audio/webm',
    bitsPerSecond: 64000,
  },
};

// Normalize expo-audio's metering value (-160..0 dB) into a 0..1 range
// suitable for waveform / glow visualisations (SCRUM-93, SCRUM-103).
export function normalizeMetering(meteringDb: number | undefined): number {
  if (meteringDb === undefined || Number.isNaN(meteringDb)) return 0;
  const FLOOR_DB = -60;
  const clamped = Math.max(FLOOR_DB, Math.min(0, meteringDb));
  return (clamped - FLOOR_DB) / -FLOOR_DB;
}

export {
  useAudioPlayer,
  useAudioPlayerStatus,
  useAudioRecorder,
  useAudioSampleListener,
  useAudioRecorderState,
} from 'expo-audio';
export type {
  AudioPlayer,
  AudioRecorder,
  AudioStatus,
  RecorderState,
} from 'expo-audio';
